import { addHours, endOfYesterday, isBefore } from 'date-fns';
import * as R from 'ramda';
import { Ability } from '../../users/ability.types';
import { UserRole } from '../../users/roles';
import { Role } from '../actions/types/actions.types';
import { isStatusAfterDraft } from '../contexts/order.utils';
import { OrderStatus } from '../interfaces/order-status.interface';
import { OrderType } from '../interfaces/orderType.enum';
import { Order, StatusType, SynchronizedType } from '../order.types';
import { OrderGatewayPaymentStatus } from './enums/orderPaymentGatewayStatus.enum';
import { OrderPaymentStatus } from './orderPayment.types';

export interface OrderPaymentActionsUtilsParams {
  order: Pick<Order<false>, 'paymentStatus' | 'status' | 'type' | 'splitted' | 'synchronized'>;
  lastGatewayPaymentStatus: OrderGatewayPaymentStatus | null;
  lastGatewayPaymentUpdatedAt: Date | null;
  requirePaymentReleaseForImmediate: boolean;
  requirePaymentReleaseForPreOrder: boolean;
  requirePaymentGateway: boolean;
  role: UserRole;
  ability: Ability;
  refundLimitHours: number;
  integrationTrigger: OrderStatus | undefined;
}

export class OrderPaymentActionsUtils {
  constructor(private readonly params: OrderPaymentActionsUtilsParams) {}

  showReleasePaymentButton = () => {
    const shouldRequirePaymentRelease =
      this.params.role === Role.ADMIN_MARCA &&
      this.params.order.paymentStatus === OrderPaymentStatus.WAITING &&
      isStatusAfterDraft(this.params.order.status as StatusType) &&
      this.params.ability.can('update', 'order-payments') &&
      R.isNil(this.params.lastGatewayPaymentStatus) &&
      this.validateDefaultParams();

    if (this.params.order.type === OrderType.PRE_ORDER) {
      return shouldRequirePaymentRelease && this.params.requirePaymentReleaseForPreOrder;
    }

    if (this.params.order.type === OrderType.READY_FOR_DELIVERY) {
      return shouldRequirePaymentRelease && this.params.requirePaymentReleaseForImmediate;
    }

    return false;
  };

  showCancelPaymentButton = () => {
    const statusesToAllowCancel = [
      OrderGatewayPaymentStatus.PENDING,
      OrderGatewayPaymentStatus.ERROR,
      OrderGatewayPaymentStatus.PAYMENT_FAILED,
    ];

    return !!(
      this.params.role === Role.ADMIN_MARCA &&
      this.params.lastGatewayPaymentStatus &&
      statusesToAllowCancel.includes(this.params.lastGatewayPaymentStatus) &&
      this.params.ability.can('update', 'order-payments') &&
      this.validateDefaultParams()
    );
  };

  showRefundPaymentButton = () => {
    const maxDateToRefund = this.params.lastGatewayPaymentUpdatedAt
      ? addHours(this.params.lastGatewayPaymentUpdatedAt, this.params.refundLimitHours)
      : endOfYesterday();

    return !!(
      this.params.role === Role.ADMIN_MARCA &&
      this.params.lastGatewayPaymentStatus &&
      this.params.lastGatewayPaymentStatus === OrderGatewayPaymentStatus.PAID &&
      this.params.ability.can('update', 'order-payments') &&
      [SynchronizedType.NOT_SYNCHRONIZED, SynchronizedType.ERROR].includes(
        this.params.order.synchronized,
      ) &&
      isBefore(new Date(), maxDateToRefund) &&
      this.validateDefaultParams()
    );
  };

  userCanMakePayment = () => {
    const validations = [
      this.params.order.paymentStatus === OrderPaymentStatus.AUTHORIZED,
      this.params.order.type === OrderType.PRE_ORDER &&
        !this.params.requirePaymentReleaseForPreOrder,
      this.params.order.type === OrderType.READY_FOR_DELIVERY &&
        !this.params.requirePaymentReleaseForImmediate &&
        this.params.ability.can('create', 'order-payments'),
    ];

    return validations.some((validation) => validation) && this.validateDefaultParams();
  };

  showMakePaymentButton = () => {
    return !!(
      this.params.lastGatewayPaymentStatus !== OrderGatewayPaymentStatus.PAID &&
      this.params.lastGatewayPaymentStatus !== OrderGatewayPaymentStatus.WAITING_REFUND &&
      this.params.ability.can('create', 'order-payments') &&
      this.validateDefaultParams()
    );
  };

  showHistoryPaymentButton = () => {
    return this.params.ability.can('read', 'order-payments') && this.validateDefaultParams();
  };

  private validateDefaultParams = () => {
    const availableStatus = [StatusType.APPROVED, StatusType.SENT] as OrderStatus[];

    if (this.params.integrationTrigger === StatusType.ON_APPROVAL) {
      availableStatus.push(StatusType.ON_APPROVAL);
    }

    return !!(
      this.params.order.splitted &&
      this.params.requirePaymentGateway &&
      availableStatus.includes(this.params.order.status)
    );
  };
}
